/**
 * Anomaly Queue model + sort (L.2.2, operator-cop-doctrine §3B.1). Pure functions
 * so the strict sort + the unified model are unit-testable (no React/Cesium).
 *
 * The queue is list-first, bound 1:1 with map markers, fed by six sources:
 *   • AI-fused high-risk hotspots (Response Level 1–4),
 *   • active Severe/Extreme CAP polygons (Response Level READ from the canonical
 *     server field cap_alerts.response_level, migration 0098 — #376; never re-derived
 *     from severity client-side),
 *   • smoke_detected anomalies (UNSCORED — "pending operator assessment"),
 *   • ice-storm + flood fusion anomalies (SCORED — rank by Response Level),
 *   • media_signal igniter spikes (#184, UNSCORED — "needs assessment").
 *
 * Sort (strict): Response Level descending, then recency descending within a level.
 * NEVER confidence. smoke_detected items are unscored — they sort at a fixed
 * "needs eyes" priority just below L3 (a major multi-site complex still outranks an
 * unconfirmed single camera flag), then by recency. media_signal igniter items
 * (#184) are likewise unscored and sit even lower (below every scored L2).
 */
import type { ActiveFireFeature, CapAlertFeature, HighRiskHotspotFeature } from '@tsr/shared-types';
import {
  accessTypeLabel,
  classifyCapLifeSafety,
  deriveOcFireWatchScore,
  escalationSafeResponseLevel,
  type CapLifeSafetyClass,
} from '@tsr/shared-types';
import { escalationSafeAnomalyResponseLevel } from './anomalyResponseLevel';
import type {
  AnomalyAssessment,
  FloodAnomalyFeature,
  IceStormAnomalyFeature,
  MediaSignalAnomalyFeature,
  MicroStatus,
  SmokeAnomalyFeature,
} from './api';
import { mediaSignalHazardLabel } from './mediaSignalAnomalyStyle';
import { URGENT_MIN_RESPONSE_LEVEL } from './styles/responseLevel';

export type QueueSource =
  | 'hotspot'
  | 'cap'
  | 'smoke'
  | 'ice_storm'
  | 'flood'
  | 'media_signal'
  // #444 source 2 — a reported wildfire whose stage goes OC (read straight off
  // active_wildland_fires; the urgent RL is the server-derived anti-flood ladder).
  | 'oc_fire';
// oc_fire is a 'fire' hazard (same 🔥 icon family as hotspots) — the SOURCE
// discriminates the data origin, the HAZARD the visual family.
export type QueueHazard = 'fire' | 'weather' | 'smoke' | 'ice_storm' | 'flood' | 'media_signal';

/** The persisted working-state (migration 0058, #140) that rides every queue
 *  feature's properties — ✓ Ack, micro-status, owner. Read into QueueItem so the
 *  panel renders server truth instead of client-side in-memory state. */
interface WorkstateProps {
  work_acknowledged_at?: string | null;
  micro_status?: MicroStatus;
  /** #331 reversible assessment axis (migration 0146). */
  assessment?: AnomalyAssessment;
  owner_label?: string | null;
}
function readWorkstate(p: WorkstateProps): {
  acknowledged: boolean;
  microStatus: MicroStatus;
  assessment: AnomalyAssessment;
  owner: string | null;
} {
  return {
    acknowledged: p.work_acknowledged_at != null,
    microStatus: p.micro_status ?? 'new',
    assessment: p.assessment ?? 'unconfirmed',
    owner: p.owner_label ?? null,
  };
}

export interface QueueItem {
  id: string;
  /** Raw surface uuid (without the `source:` prefix) — the workstate write key. */
  sourceId: string;
  source: QueueSource;
  hazard: QueueHazard;
  /** IMS Response Level 1–4, or null for unscored smoke_detected items. */
  responseLevel: number | null;
  /** Sort priority: RL for scored items; SMOKE_RANK for smoke (just below L3). */
  rank: number;
  hazardIcon: string;
  title: string;
  /** ISO recency timestamp (observed/created/detected). */
  timeIso: string;
  /** Last in-place escalation stamp (#281), ice/flood only — set when an open
   *  anomaly's Response Level rises or its class promotes. The #272 wall risk
   *  tour treats the LATEST of timeIso and this as the item's recency, so an
   *  in-place worsening re-arms a cleared SITREP card. Undefined/null for
   *  families that don't escalate in place (hotspot/smoke/media-signal). */
  escalatedAtIso?: string | null;
  /** Source kicker shown on line 2 ("AI Fusion · conf 82" / "Alert Ready (NPAS)" / "Camera"). */
  kicker: string;
  /** One-line corroboration summary for fused items (null otherwise). */
  corroboration: string | null;
  /** #447 Track A Slice 3b — a COMPACT demotion reason for a hotspot that was DEMOTED off
   *  urgent (RL ≤ 2) by the directional connected-fire inversion (the known fire IS the
   *  alert) or the Slice-2 static-heat mask. e.g. "demoted · known fire <name>" /
   *  "demoted · industrial heat". Rendered as a muted chip on the queue card. null when
   *  the item was not demoted (every non-hotspot source, and every undemoted hotspot). */
  demotionReason?: string | null;
  /** #448 Track A Slice 3c — a POSITIVE callout for a hotspot the values-at-risk VETO kept
   *  on the urgent surface ("kept urgent · 8 km from Pikangikum 14 · in wind path"). Rendered as
   *  a distinct emerald chip (NOT the amber demotion chip — a veto is a keep, not a demote).
   *  null when the veto did not fire. Mutually exclusive with demotionReason in practice. */
  valuesKeptUrgent?: string | null;
  /** Server-persisted ✓ Ack (migration 0058, #140) — seeds the collapsed section
   *  and survives reload / shift handover. */
  acknowledged: boolean;
  /** Server-persisted micro-status (new → working → resolving). */
  microStatus: MicroStatus;
  /** #331 server-persisted reversible assessment (unconfirmed/confirmed/false_positive);
   *  survives reload + shift handover. Defaults 'unconfirmed' when no workstate row. */
  assessment: AnomalyAssessment;
  /** Server-persisted free-text owner / position, or null. NOT incident ownership — this is the
   *  honor-system SEAT claim (owner_label). Do NOT use it for the orphan predicate. */
  owner: string | null;
  /** ★ The OWNING incident's id (the provenance-linked, LIVE incident_command lead), or null when
   *  un-owned. Distinct from `owner` (a seat claim) and `linkedIncidentId` (a CAP-collapse link).
   *  Drives the ORPHAN filter (un-owned + Threat Level ≥ 3): the hazard nobody has assumed command
   *  of. Set from owned_incident when its status is 'open' (a closed-incident ref re-surfaces as an
   *  orphan). Optional so pre-existing QueueItem fixtures stay valid; absent ⟺ un-owned (isOrphan
   *  reads `== null`, which covers undefined). */
  ownedIncidentId?: string | null;
  /** R2/#478 — the CAP life-safety class (evacuation | life_safety | routine) from the
   *  mig-0098-anchored classifier. Set on CAP-sourced rows only (undefined otherwise).
   *  Only a non-routine CAP is an auto-link candidate / eligible for the incident
   *  "Link to incident" dropdown. */
  lifeSafetyClass?: CapLifeSafetyClass;
  /** R2/#478 — the incident this CAP has collapsed INTO (persisted manual link OR the
   *  read-time auto-link projection). Non-null ⇒ the row is dropped from the standalone
   *  queue (it renders as that incident's protective-action overlay, not a peer alert). */
  linkedIncidentId?: string | null;
  lat: number;
  lon: number;
}

/** smoke_detected priority: needs operator eyes, but an unconfirmed single camera
 *  flag must not outrank a real Level-3 major complex. Sits between L2 and L3. */
export const SMOKE_RANK = 2.5;

/** media_signal igniter priority (#184): needs assessment, but it is third-party
 *  chatter — even lower confidence than a camera CV hit (a sensor we operate). It
 *  must never outrank a SCORED Level-2 event: sits between L1 and L2. */
export const MEDIA_SIGNAL_RANK = 1.5;

// #376 — the CAP severity→Response Level mapping is GONE from the client. The
// canonical Response Level is now computed SERVER-SIDE (cap_alerts.response_level,
// migration 0098, the de-flood-safe severity+event gate) and read straight off the
// feature properties, so the queue, the map, and the de-flood count never diverge.

const HAZARD_ICON: Record<QueueHazard, string> = {
  fire: '🔥',
  weather: '⚠️',
  smoke: '💨',
  ice_storm: '❄️',
  flood: '🌊',
  media_signal: '📡',
};

/**
 * Operator-facing hazard labels (W6 raw-enum legibility, ledger §3.D) so the hazard
 * filter chips + group headers never leak `ice_storm` / `media_signal`. `media_signal`
 * reads "News & Social" — NOT "Media" — so it never cold-reads as the App A #62 Public
 * Information Management function.
 */
export const HAZARD_LABEL: Record<QueueHazard, string> = {
  fire: 'Fire',
  weather: 'Weather',
  smoke: 'Smoke',
  ice_storm: 'Ice Storm',
  flood: 'Flood',
  media_signal: 'News & Social',
};

/** Label for a hazard-filter chip / group header, including the `all` pseudo-value. */
export function hazardFilterLabel(hazard: QueueHazard | 'all'): string {
  return hazard === 'all' ? 'All' : HAZARD_LABEL[hazard];
}

function hotspotCorroboration(s: HighRiskHotspotFeature['properties']['corroborating_signals']): string {
  const bits: string[] = [];
  if (s?.satellite_corroboration) bits.push('satellite');
  if (s?.fwi != null && s.fwi > 10) bits.push('FWI');
  if (s?.hms_overlap) bits.push('smoke plume');
  if (s?.active_fire_nearby) bits.push('adjacent fire');
  if (s && s.aqhi_nearby_count > 0) bits.push('air quality');
  // camera_smoke_confirmed is added by the list API but not in the shared type.
  if ((s as { camera_smoke_confirmed?: boolean } | undefined)?.camera_smoke_confirmed) {
    bits.push('camera smoke');
  }
  return bits.length > 0 ? bits.join(' + ') : 'base detection';
}

/**
 * #447 Track A Slice 3b — the compact queue-card demotion reason for a fused hotspot.
 * A connected-fire demotion names the known fire ("demoted · known fire <name>") so the
 * operator reads WHY a hotspot near a reported fire is muted (it IS that fire's spread,
 * already alerting). The Slice-2 static-heat mask reads "demoted · industrial heat".
 * null when the hotspot was not demoted. Reads the compact list-summary fields the API
 * projects (no detail fetch).
 */
export function hotspotDemotionReason(
  s: HighRiskHotspotFeature['properties']['corroborating_signals'],
): string | null {
  if (!s) return null;
  if (s.demoted_reason === 'connected_fire') {
    return s.connected_fire_name
      ? `demoted · known fire ${s.connected_fire_name}`
      : 'demoted · known fire';
  }
  if (s.demoted_reason === 'static_heat' || s.static_heat_masked) {
    return 'demoted · industrial heat';
  }
  return null;
}

/**
 * #448 Track A Slice 3c — the positive "kept urgent — N km from <place>" callout for a
 * hotspot the values-at-risk VETO held on the urgent surface (a genuine, unconnected fire
 * near a NATIONAL populated place). Reads the compact list-summary fields the API projects;
 * marks the downwind (evacuation-risk) case. null when the veto did not fire.
 */
export function hotspotValuesKeptUrgent(
  s: HighRiskHotspotFeature['properties']['corroborating_signals'],
): string | null {
  if (!s?.values_kept_urgent || !s.values_place_name) return null;
  const d = s.values_place_distance_m;
  const where =
    d == null || d < 100
      ? `at ${s.values_place_name}`
      : `${d < 10_000 ? (d / 1000).toFixed(1) : String(Math.round(d / 1000))} km from ${s.values_place_name}`;
  // #662 — the bare word "downwind" is not an operator string (§4.2 keeps it a backend field);
  // align with the #647 register. The suffix is a POSITIVE fact appended ONLY on `=== true`
  // (fusion.ts projects the shared 3-state cone `=== true`, so `false`/absent = no claim — an
  // UNKNOWN wind is never rendered as "not in wind path").
  return `kept urgent · ${where}${s.values_place_downwind ? ' · in wind path' : ''}`;
}

/** The OWNING incident id when a hazard is owned by a LIVE (non-closed) incident, else null.
 *  owned ⟺ owned_incident present AND status 'open' — a closed-incident ref re-surfaces the hazard
 *  as an orphan consistently across sources (fires drop the ref; hotspots keep it as 'closed'). */
function ownedIncidentIdOf(
  owned: { id: string; status: 'open' | 'closed' } | null | undefined,
): string | null {
  return owned && owned.status === 'open' ? owned.id : null;
}

export function hotspotToQueueItem(f: HighRiskHotspotFeature): QueueItem {
  const p = f.properties;
  const [lon, lat] = f.geometry.coordinates;
  // #445-ws2 — the ESCALATION-SAFE Response Level (the owning IC's operator pin layered over
  // the derived): an IC escalation lifts the queue row; a de-escalation can never bury the
  // derived. `operator_response_level` is null for a non-owned hotspot ⇒ pure derived.
  const rl = escalationSafeResponseLevel(p.operator_response_level ?? null, p.response_level) ?? p.response_level;
  return {
    id: `hotspot:${p.id}`,
    sourceId: p.id,
    source: 'hotspot',
    hazard: 'fire',
    responseLevel: rl,
    rank: rl,
    hazardIcon: HAZARD_ICON.fire,
    title: `High-Risk Hotspot — ${lat.toFixed(2)}, ${lon.toFixed(2)}`,
    timeIso: p.observed_at,
    kicker: `AI Fusion · conf ${p.confidence}`,
    corroboration: hotspotCorroboration(p.corroborating_signals),
    demotionReason: hotspotDemotionReason(p.corroborating_signals),
    valuesKeptUrgent: hotspotValuesKeptUrgent(p.corroborating_signals),
    ownedIncidentId: ownedIncidentIdOf(p.owned_incident),
    ...readWorkstate(p),
    lat,
    lon,
  };
}

/**
 * OC-fire urgent alert → queue item (#444, source 2). Reads the SERVER-derived
 * urgent Response Level (oc_fire_response_level, the anti-flood ladder) straight off
 * the feature — the client never re-derives it (same discipline as CAP #376). Names
 * the threatened community in the title so the municipal seat can act ("OC Wildfire ·
 * 6 km SW of Marten River"). Callers surface ONLY RL≥3 OC fires (the muted RL2 floor
 * stays a map glyph, never the queue). Reuses the `fire` hazard icon + scored ranking.
 */
export function ocFireToQueueItem(f: ActiveFireFeature): QueueItem {
  const p = f.properties;
  const [lon, lat] = p.centroid;
  // #445-ws2 — the ESCALATION-SAFE Response Level: the owning IC's operator pin layered over
  // the derived OC ladder (an escalation lifts the queue row; a de-escalation can never bury a
  // worse derived). Callers only reach this for an OC fire (derived non-null); a non-owned OC
  // fire (operator_response_level null) resolves to the pure derived. NEVER re-derives the ladder.
  const rl = escalationSafeResponseLevel(p.operator_response_level ?? null, p.oc_fire_response_level ?? null);
  const fireName =
    p.fire_id != null
      ? `${p.agency ? `${p.agency} ` : ''}${p.fire_id}`
      : `${lat.toFixed(2)}, ${lon.toFixed(2)}`;
  const tc = p.threatened_community;
  // #632/#635 — the ACCESS FACT rides onto the queue subtitle so the PEOC duty officer sees the
  // access mode when scanning ("18 km NE of Sandy Lake (Access: fly-in)"). Fact-only, never the
  // "no road out" conclusion (founder 2026-07-13). Road communities add nothing; 'unknown' reads
  // "access unconfirmed" (never reassuring).
  const accessLabel = accessTypeLabel(tc?.access_type);
  const threatPhrase =
    tc?.name != null && tc.distance_m != null
      ? `${Math.round(tc.distance_m / 1000)} km ${tc.compass ?? ''} of ${tc.name}${accessLabel ? ` (${accessLabel})` : ''}`.replace(
          /\s+of/,
          ' of',
        )
      : null;
  const title = threatPhrase ? `OC Wildfire · ${threatPhrase}` : `OC Wildfire — ${fireName}`;
  // #618 Defect 2 (fail-loud): mark a fire whose escalation signal went stale — its only claim
  // to urgency was a now-untrusted ≥60 score, so it is kept on the queue (candidates drawer)
  // rather than silently dropped. The map disc stays honestly muted; this marker tells the
  // operator the fire needs re-assessment (its watching score is degraded, not benign).
  const degraded =
    deriveOcFireWatchScore({
      ocFireResponseLevel: p.oc_fire_response_level ?? null,
      escalationIndex: p.escalation_index ?? null,
      escalationUpdatedAt: p.escalation_updated_at ?? null,
      now: Date.now(),
    })?.kind === 'degraded';
  const kparts: string[] = [p.agency ?? 'CWFIS'];
  if (degraded) kparts.push('⚠ watching degraded');
  if (p.hectares_estimated != null)
    kparts.push(`${Math.round(p.hectares_estimated).toLocaleString()} ha`);
  if (p.severe_cap_proximate) kparts.push(`severe CAP${p.severe_cap_event ? ` (${p.severe_cap_event})` : ''}`);
  // #667 — when the fire is in the wind path of MULTIPLE communities, say so on the queue row (each
  // nameable on the panel). Reads the HONEST three-state threatened_communities[].downwind (=== true
  // only; NEVER the flattened hotspot boolean, NEVER null-as-in-path). Additive: 0/1 in-path leaves
  // the row byte-identical (the single nearest is already named in the title). NO imperative.
  const inWindPathCount = (p.threatened_communities ?? []).filter((c) => c.downwind === true).length;
  if (inWindPathCount >= 2) kparts.push(`${inWindPathCount} communities in wind path`);
  return {
    id: `oc_fire:${p.id}`,
    sourceId: p.id,
    source: 'oc_fire',
    hazard: 'fire',
    responseLevel: rl,
    // Callers only surface RL≥3 OC fires, so rl is set; the floor (2) is a defensive
    // fallback that never sorts a real queued OC fire.
    rank: rl ?? 2,
    hazardIcon: HAZARD_ICON.fire,
    title,
    timeIso: p.observed_at,
    kicker: kparts.join(' · '),
    // #447 — the co-located-hotspot fold (#444 blocker #8) is RETIRED; the hotspot-side
    // suppression now happens at source (a known fire's downwind spread is demoted in
    // fusion), so the OC-fire item no longer carries a fold id.
    corroboration: threatPhrase ? `stage OC · ${fireName}` : 'stage OC',
    ownedIncidentId: ownedIncidentIdOf(p.owned_incident),
    ...readWorkstate(p),
    lat,
    lon,
  };
}

export function capToQueueItem(f: CapAlertFeature): QueueItem {
  const p = f.properties;
  // CAP geometry is a MultiPolygon; use the first ring's first vertex as the anchor.
  const coords = (f.geometry as { coordinates?: unknown }).coordinates;
  let lon = 0;
  let lat = 0;
  try {
    // MultiPolygon → [poly][ring][vertex][lon,lat]
    const v = (coords as number[][][][])[0]?.[0]?.[0];
    if (v) {
      lon = v[0] ?? 0;
      lat = v[1] ?? 0;
    }
  } catch {
    /* leave 0,0 */
  }
  // #376 — read the CANONICAL server Response Level (cap_alerts.response_level).
  // The de-flood-safe severity+event gate lives in the DB, so a non-life-safety
  // Severe (squall/thunderstorm) arrives here already demoted to RL2 (candidate),
  // exactly as the map and the de-flood count see it. Fall back to RL1 only if the
  // field is somehow absent (an old API build) — never re-derive from severity.
  const rl = p.response_level ?? 1;
  // R2/#478 — classify the CAP against the mig-0098-anchored life-safety gate so the
  // queue/panel know whether it is an evac/life-safety alert (auto-link candidate;
  // gets the "Link to incident" dropdown) vs routine. `incident_id` is the EFFECTIVE
  // link the API returns (persisted manual link OR the read-time auto-link projection)
  // — non-null ⇒ the row has collapsed into that incident and partitionQueue drops it.
  const lifeSafetyClass = classifyCapLifeSafety({ event: p.event, responseLevel: p.response_level });
  return {
    id: `cap:${p.id}`,
    sourceId: p.id,
    source: 'cap',
    hazard: 'weather',
    responseLevel: rl,
    rank: rl,
    hazardIcon: HAZARD_ICON.weather,
    title: `${p.event ?? 'Weather warning'}${p.area_desc ? ` — ${p.area_desc}` : ''}`,
    timeIso: p.sent,
    // #159 — surface NPAS / Alert Ready provenance on CAP-sourced rows (these
    // arrive over NAAD, the broadcast backbone of Canada's National Public
    // Alerting System, branded "Alert Ready" to the public).
    kicker: `Alert Ready (NPAS) · ${p.severity ?? 'severe'}`,
    corroboration: null,
    lifeSafetyClass,
    linkedIncidentId: p.incident_id ?? null,
    // A CAP carries a collapse link (linkedIncidentId), NOT a provenance-owned incident; it never
    // rides the ownership axis. Explicitly un-owned so it can be an orphan candidate at RL≥3.
    ownedIncidentId: null,
    ...readWorkstate(p),
    lat,
    lon,
  };
}

export function smokeToQueueItem(f: SmokeAnomalyFeature): QueueItem {
  const p = f.properties;
  const [lon, lat] = f.geometry.coordinates;
  return {
    id: `smoke:${p.id}`,
    sourceId: p.id,
    source: 'smoke',
    hazard: 'smoke',
    responseLevel: null,
    rank: SMOKE_RANK,
    hazardIcon: HAZARD_ICON.smoke,
    title: `Smoke Detected — ${p.camera_name}`,
    timeIso: p.detected_at,
    kicker: p.confidence != null ? `Camera · conf ${p.confidence}` : 'Camera CV',
    corroboration: null,
    ownedIncidentId: null, // unscored (responseLevel null) — never an orphan (needs assessment, not command)
    ...readWorkstate(p),
    lat,
    lon,
  };
}

/**
 * Ice-storm anomaly (SCORED — carries a Response Level, so it ranks by RL exactly
 * like a hotspot, NOT at the smoke "needs eyes" rank). Title leads with the cluster
 * scale; the anticipatory/impact frame rides the kicker.
 */
export function iceStormToQueueItem(f: IceStormAnomalyFeature): QueueItem {
  const p = f.properties;
  const [lon, lat] = f.geometry.coordinates;
  const cust = p.customers_affected;
  const lead = p.utilities[0];
  const ws = readWorkstate(p);
  // #445-ws2 — the ESCALATION-SAFE Response Level (the owning IC's operator pin layered over the
  // derived): an IC escalation lifts the queue row; a de-escalation can never bury a worse derived.
  // `operator_response_level` is null for a non-owned anomaly ⇒ the pure derived (queue unchanged).
  const rl = escalationSafeAnomalyResponseLevel(p);
  return {
    id: `ice:${p.id}`,
    sourceId: p.id,
    source: 'ice_storm',
    hazard: 'ice_storm',
    responseLevel: rl,
    rank: rl,
    hazardIcon: HAZARD_ICON.ice_storm,
    title: `Ice Storm — ${p.outage_count} outages${lead ? ` · ${lead}` : ''}`,
    timeIso: p.first_detected_at,
    escalatedAtIso: p.escalated_at ?? null,
    kicker:
      p.anomaly_type === 'anticipatory'
        ? `Outage cluster · ${p.cap_event ?? 'ice warning'}`
        : 'Outage cluster · impact',
    corroboration: cust != null ? `${cust.toLocaleString()} customers` : null,
    ownedIncidentId: ownedIncidentIdOf(p.owned_incident),
    ...ws,
    // OR the unified workstate ack with the source's own status (a detail-panel ack
    // flips status; the 0058 backfill seeds workstate — either implies acknowledged).
    acknowledged: ws.acknowledged || p.status === 'acknowledged',
    lat,
    lon,
  };
}

/**
 * Flood anomaly (SCORED — carries a Response Level, so it ranks by RL exactly like a
 * hotspot/ice anomaly). Title leads with the river + rate; the watch/warning frame
 * rides the kicker.
 */
export function floodToQueueItem(f: FloodAnomalyFeature): QueueItem {
  const p = f.properties;
  const [lon, lat] = f.geometry.coordinates;
  const rate = p.rate_of_rise_cm_per_hr;
  const ws = readWorkstate(p);
  // #445-ws2 — the ESCALATION-SAFE Response Level (the owning IC's operator pin layered over the
  // derived): an IC escalation lifts the queue row; a de-escalation can never bury a worse derived.
  // `operator_response_level` is null for a non-owned anomaly ⇒ the pure derived (queue unchanged).
  const rl = escalationSafeAnomalyResponseLevel(p);
  return {
    id: `flood:${p.id}`,
    sourceId: p.id,
    source: 'flood',
    hazard: 'flood',
    responseLevel: rl,
    rank: rl,
    hazardIcon: HAZARD_ICON.flood,
    title: `Flood — ${p.river ?? p.station_name ?? p.station_number} · ${rate.toFixed(1)} cm/hr`,
    timeIso: p.first_detected_at,
    escalatedAtIso: p.escalated_at ?? null,
    kicker:
      p.anomaly_type === 'warning'
        ? 'Rapid rise · gauge over threshold'
        : 'Rising · gauge over threshold',
    corroboration:
      p.communities_in_reach > 0
        ? `${p.communities_in_reach} ${p.communities_in_reach === 1 ? 'community' : 'communities'} in reach`
        : null,
    ownedIncidentId: ownedIncidentIdOf(p.owned_incident),
    ...ws,
    // OR the unified workstate ack with the source's own status (see ice above).
    acknowledged: ws.acknowledged || p.status === 'acknowledged',
    lat,
    lon,
  };
}

/**
 * Media-signal igniter anomaly (#184 — UNSCORED, like smoke: "needs assessment",
 * never a Response Level). Title leads with the hazard chatter frame; the
 * outlet/story counts ride the kicker so the operator reads the evidence scale
 * before opening the panel.
 */
export function mediaSignalToQueueItem(f: MediaSignalAnomalyFeature): QueueItem {
  const p = f.properties;
  const [lon, lat] = f.geometry.coordinates;
  const place = p.evidence.signals.find((s) => s.geo_name)?.geo_name;
  return {
    id: `media:${p.id}`,
    sourceId: p.id,
    source: 'media_signal',
    hazard: 'media_signal',
    responseLevel: null,
    rank: MEDIA_SIGNAL_RANK,
    hazardIcon: HAZARD_ICON.media_signal,
    title: `${mediaSignalHazardLabel(p.hazard_key)}${place ? ` — ${place}` : ''}`,
    timeIso: p.first_detected_at,
    kicker: `Media wire · ${p.source_count} outlets · ${p.signal_count} stories`,
    corroboration: p.status === 'confirmed' ? 'operator-confirmed' : null,
    ownedIncidentId: null, // unscored — never an orphan
    ...readWorkstate(p),
    lat,
    lon,
  };
}

/* ───────────────────── Life-safety SAME-RANK tiebreak (#479, R4) ───────────────── *
 *  R4 (founder 2026-06-30, ratified by the W2 council #476): sortQueue was rank →
 *  recency only, so at EQUAL Response Level a fresh hotspot buried an older life-safety
 *  CAP (an RL3 tornado sank under newer RL3 hotspots). This hazard-class priority sits
 *  STRICTLY between rank and recency — it orders a TIE only and NEVER crosses RL bands
 *  (an RL3 item never sorts above an RL4; QF5). Keyed off QueueItem.hazard:
 *    • life-safety   — weather (CAP) + flood. At RL≥3 these ARE the migration-0098
 *                      life-safety set (tornado / flash-flood / storm-surge / evacuation).
 *    • active hazard — fire (hotspot / oc_fire) + ice_storm.
 *    • detection     — smoke + media_signal (already sub-ranked BELOW every scored item
 *                      by their fixed `rank`, so this only orders detection-vs-detection
 *                      ties, all equal → falls straight to recency).
 *  Named residuals (QF6, not solved here): same-hazard RL3 ties still fall to recency
 *  (fire-buries-fire — acceptable); a single GLOBAL order can sink a fire-owner's own RL3
 *  incident below an out-of-area tornado (a W3-ownership follow-on). Tweak = THIS table.
 * ──────────────────────────────────────────────────────────────────────────────── */
const HAZARD_PRIORITY: Record<QueueHazard, number> = {
  weather: 3, // life-safety (CAP — tornado / flash-flood / storm-surge at RL≥3)
  flood: 3, // life-safety
  fire: 2, // active hazard (hotspot / oc_fire)
  ice_storm: 2, // active hazard
  smoke: 1, // detection (already below every scored item via SMOKE_RANK)
  media_signal: 1, // detection (already below every scored item via MEDIA_SIGNAL_RANK)
};

/** Life-safety class priority for the SAME-RANK tiebreak (#479). Higher sorts first at
 *  EQUAL Response Level. Pure function of QueueItem.hazard — never crosses RL bands. */
export function hazardPriority(it: QueueItem): number {
  return HAZARD_PRIORITY[it.hazard];
}

/**
 * Strict sort: rank (Response Level) desc, then — at EQUAL rank — life-safety hazard
 * class desc (#479; QF5 same-rank only, never crosses bands), then recency desc.
 */
export function sortQueue(items: QueueItem[]): QueueItem[] {
  return [...items].sort((a, b) => {
    if (b.rank !== a.rank) return b.rank - a.rank;
    // #479 — life-safety SAME-RANK tiebreak: an RL3 tornado/flash-flood outranks a
    // newer RL3 hotspot. Strictly between rank and recency; never crosses RL bands.
    if (hazardPriority(b) !== hazardPriority(a)) return hazardPriority(b) - hazardPriority(a);
    const ta = Date.parse(a.timeIso) || 0;
    const tb = Date.parse(b.timeIso) || 0;
    return tb - ta;
  });
}

export interface QueueReconcileResult {
  /** Surviving DISPLAYED items, in their existing display order, with content
   *  refreshed from `incoming`. Order is NEVER re-sorted here — a mid-triage
   *  arrival must not reshuffle the row under the operator's cursor (#143). */
  items: QueueItem[];
  /** Arrivals held behind the "N new ↑" header — surviving prior pending items
   *  plus brand-new ones, refreshed from `incoming`. Merged into `items` (and
   *  re-sorted) only when the operator clicks the header. */
  pending: QueueItem[];
}

/**
 * Arrival-stability reconcile (operator-cop-doctrine §3B.1, #143). Given the
 * currently-displayed rows, the rows already waiting behind the "N new" header,
 * and a freshly-fetched `incoming` set, decide what stays put and what is held:
 *
 *  • Displayed rows that still exist keep their exact position (content refreshed)
 *    — frozen ordering, so an ack/open never lands on the wrong item.
 *  • Displayed/pending rows that vanished from `incoming` drop out (auto-leave).
 *  • Rows in `incoming` that are neither displayed nor already pending become NEW
 *    arrivals, parked in `pending` rather than spliced into the live order.
 */
export function reconcileQueue(
  displayed: QueueItem[],
  pending: QueueItem[],
  incoming: QueueItem[],
): QueueReconcileResult {
  const incomingById = new Map(incoming.map((it) => [it.id, it]));
  const nextItems = displayed
    .filter((it) => incomingById.has(it.id))
    .map((it) => incomingById.get(it.id)!);
  const displayedIds = new Set(nextItems.map((it) => it.id));
  const survivingPending = pending
    .filter((it) => incomingById.has(it.id) && !displayedIds.has(it.id))
    .map((it) => incomingById.get(it.id)!);
  const pendingIds = new Set(survivingPending.map((it) => it.id));
  const added = incoming.filter((it) => !displayedIds.has(it.id) && !pendingIds.has(it.id));
  return { items: nextItems, pending: [...survivingPending, ...added] };
}

/** Count chips: L4/L3/L2/L1 (scored items) + smoke "pending" + media "needs
 *  assessment" counts (the two unscored registers stay out of the L1 chip). */
export interface QueueCounts {
  l4: number;
  l3: number;
  l2: number;
  l1: number;
  smoke: number;
  media: number;
}

export function countQueue(items: QueueItem[]): QueueCounts {
  const c: QueueCounts = { l4: 0, l3: 0, l2: 0, l1: 0, smoke: 0, media: 0 };
  for (const it of items) {
    if (it.source === 'smoke') c.smoke += 1;
    else if (it.source === 'media_signal') c.media += 1;
    else if (it.responseLevel === 4) c.l4 += 1;
    else if (it.responseLevel === 3) c.l3 += 1;
    else if (it.responseLevel === 2) c.l2 += 1;
    else c.l1 += 1;
  }
  return c;
}

/* ─────────────────── Urgent-list de-flood partition (#394, R3) ─────────────────── *
 *  R3 (founder 2026-06-30, ratified by the W2 council #476): the alert LIST carried the
 *  full L1–L4 set, so the #370-demoted RL1/RL2 candidates FLOODED the urgent bar — the
 *  MAP de-floods to RL≥3 (FusionCandidatesLayer carries the demoted mass off the urgent
 *  surface), but the LIST never did (the deferred #394 call; see isPanelAckEligible). THIS
 *  is the ONE shared partition every queue surface splits through (QF1), so the urgent bar,
 *  the urgent map layer (#370), and the #446 SITREP urgent count never disagree — they all
 *  key off the SAME URGENT_MIN_RESPONSE_LEVEL gate.
 * ──────────────────────────────────────────────────────────────────────────────── */

/**
 * Whether a queue item belongs on the URGENT triage surface (vs the "Fusion candidates"
 * drawer). Urgent = a SCORED red (Response Level ≥ URGENT_MIN_RESPONSE_LEVEL — the same
 * gate the urgent map layer + the SITREP urgent count use) OR an UNSCORED detection (smoke
 * / media_signal, responseLevel == null), which has no level to gate on and is ALWAYS a
 * "needs assessment" item the operator must still see (QF2). Everything else — a scored
 * RL1/RL2 candidate — falls to the drawer. This is the SAME predicate as isPanelAckEligible
 * ("the urgent surface = scored reds + unscored"); they read in lockstep by construction.
 */
export function isUrgentListItem(it: QueueItem): boolean {
  return it.responseLevel == null || it.responseLevel >= URGENT_MIN_RESPONSE_LEVEL;
}

/**
 * ★ ORPHAN (feat/ownership-coordination) — an UN-OWNED hazard at Threat Level ≥ 3: the hazard
 * nobody has assumed Incident Command of, the state that kills ("a hotspot near a First Nation
 * sits at 'confirmed' for six hours; the municipality assumes the province has it, the province
 * assumes it's municipal; nobody's phone rings"). It is the PEOC's #1 ask and a free by-product now
 * that ownership rides every hazard. Un-owned = `ownedIncidentId == null` (a closed-incident ref
 * already resolves to null via ownedIncidentIdOf, so a re-opened hazard re-surfaces as an orphan).
 * The gate is the MACHINE THREAT axis (responseLevel here is the derived level; #613 — NEVER call
 * it "Response Level" on this surface); an unscored detection (responseLevel null) is a "needs
 * assessment" item, not an orphan, so it is correctly excluded.
 */
export function isOrphan(it: QueueItem): boolean {
  return it.ownedIncidentId == null && (it.responseLevel ?? 0) >= URGENT_MIN_RESPONSE_LEVEL;
}

export interface QueuePartition {
  /** The urgent triage surface: scored reds (RL≥3) + unscored detections (smoke/media). */
  urgent: QueueItem[];
  /** Sub-urgent SCORED candidates (RL1/RL2) — the #370-demoted mass, one click away in
   *  the collapsible "Fusion candidates" drawer. The MAP renders these (FusionCandidates
   *  layer); they are NEVER silently dropped. */
  candidates: QueueItem[];
  /** R2/#478 — items that have COLLAPSED INTO an incident as a protective-action overlay
   *  (an evac/life-safety CAP whose effective incident link is set, auto or manual).
   *  Pulled out of urgent/candidates so a linked evac is NEVER a competing queue row —
   *  it surfaces under its incident's work surface instead. Never silently dropped. */
  collapsed: QueueItem[];
}

/** R2/#478 — a queue item that has collapsed into an incident (its evac/CAP effective
 *  link is set). Only evac/CAP rows carry `linkedIncidentId`; every other source is
 *  never collapsed. */
export function isCollapsedIntoIncident(it: QueueItem): boolean {
  return it.linkedIncidentId != null;
}

/**
 * THE shared queue partition (#394, QF1; R2/#478 collapse). Splits items into the urgent
 * surface, the sub-urgent "Fusion candidates" drawer, and the "collapsed into incident"
 * bucket. A CAP that has linked to an incident (auto or manual) leaves the standalone
 * queue entirely (it renders as that incident's protective-action overlay) — checked
 * FIRST so a linked RL≥3 evac never lands on the urgent bar as a peer alert. Every queue
 * surface uses THIS, so the urgent bar and the drawer never disagree per-component. Order
 * within each bucket is preserved (callers sort upstream), so the partition never
 * reshuffles a row under the operator's cursor (the #143 arrival-stability guarantee; QF4).
 */
export function partitionQueue(items: QueueItem[]): QueuePartition {
  const urgent: QueueItem[] = [];
  const candidates: QueueItem[] = [];
  const collapsed: QueueItem[] = [];
  for (const it of items) {
    if (isCollapsedIntoIncident(it)) collapsed.push(it);
    else if (isUrgentListItem(it)) urgent.push(it);
    else candidates.push(it);
  }
  return { urgent, candidates, collapsed };
}

/* ───────────────────────── Standing-alarm ceiling (#129) ──────────────────── *
 *  design-council-2026-06-05.md Part II rule 5 (EEMUA 191 standing-alarm
 *  discipline): never let a wall of permanent red normalize. When the unacked
 *  URGENT (Response Level 3/4 — the pulsing reds) count exceeds the console
 *  ceiling, OR the oldest unacked red has stood longer than one operational shift,
 *  the queue shows a "N unacknowledged · oldest Xh — triage mode" health strip.
 * ───────────────────────────────────────────────────────────────────────────── */

/** EEMUA "standing alarms <10/console". Above this many unacked reds → triage. */
export const STANDING_ALARM_COUNT_CEILING = 10;
/** One operational shift (hours). A red standing longer than this → triage. */
export const STANDING_ALARM_SHIFT_HOURS = 12;

export interface StandingAlarmStatus {
  /** Unacked urgent (L3/L4) count. */
  count: number;
  /** Age of the oldest unacked urgent, whole hours. */
  oldestHours: number;
}

/** True when an item is an UNACKNOWLEDGED urgent red — Response Level 3/4 and not
 *  yet acknowledged (smoke is pre-fusion amber, never urgent; L1/L2 are muted).
 *  Exported as THE shared urgency predicate: the wall ticker takeover (#263) and
 *  the wall hero's Urgent count chip (#268 option (a)) must agree with the
 *  standing-alarm rule item-for-item — one predicate, no mirrors. */
export function isUnackedUrgent(it: QueueItem): boolean {
  return !it.acknowledged && (it.responseLevel ?? 0) >= URGENT_MIN_RESPONSE_LEVEL;
}

/* ───────────────────────── Detail-panel inline Ack (#391) ──────────────────── *
 *  Whether a detail-panel body should offer an inline ✓ Ack for this item —
 *  ACK STATE aside (that's `it.acknowledged`: an eligible item shows the ✓ Ack
 *  control when unacked and an "Acknowledged" chip once acked). This is
 *  INTENTIONALLY NARROWER than the queue ROW's ✓ Ack: the queue list carries the
 *  full L1–L4 set and acks every unacked row regardless of level, whereas the
 *  detail-panel ack is scoped to the URGENT surface (the dots a map click can
 *  reach), so it does NOT re-introduce panel acks for the #370-demoted candidates:
 *    • a SCORED item (hotspot / cap) is eligible only in the URGENT band (RL ≥
 *      URGENT_MIN_RESPONSE_LEVEL) — matching the urgent MAP layer; so a demoted
 *      L1/L2 candidate or a sub-Severe CAP gets no panel Ack. Combined with the
 *      unacked check this is exactly `isUnackedUrgent` (the same urgency predicate).
 *    • an UNSCORED item (smoke / media_signal — responseLevel null) has no level to
 *      gate on and is always a "needs assessment" item, so it is always eligible
 *      (the founder's #391 ask explicitly covers smoke + media).
 *  Ice-storm / flood keep their legacy own-status ack path — out of scope here.
 *  (#394 has since gated the queue LIST to this SAME surface — see isUrgentListItem /
 *   partitionQueue; this predicate and the list partition read in lockstep, so it now
 *   delegates to isUrgentListItem rather than re-stating the gate.)
 * ───────────────────────────────────────────────────────────────────────────── */
export function isPanelAckEligible(it: QueueItem): boolean {
  return isUrgentListItem(it);
}

/**
 * Resolve the standing-alarm health status (Part II rule 5). Returns null when the
 * queue is within EEMUA discipline (no strip); otherwise the count + oldest-age to
 * render "N unacknowledged · oldest Xh — triage mode". `nowMs` is injected so it is
 * pure + unit-testable.
 */
export function standingAlarmStatus(
  items: QueueItem[],
  nowMs: number,
): StandingAlarmStatus | null {
  const urgents = items.filter(isUnackedUrgent);
  if (urgents.length === 0) return null;
  const oldestMs = Math.min(...urgents.map((it) => Date.parse(it.timeIso) || nowMs));
  const oldestHours = Math.floor(Math.max(0, nowMs - oldestMs) / 3_600_000);
  const tooMany = urgents.length > STANDING_ALARM_COUNT_CEILING;
  const tooOld = oldestHours >= STANDING_ALARM_SHIFT_HOURS;
  if (!tooMany && !tooOld) return null;
  return { count: urgents.length, oldestHours };
}

// #447 Track A Slice 3b RETIRED foldCoLocatedHotspots (#444 blocker #8 — the client-side
// queue de-dupe stopgap). The hotspot-side suppression now happens AT SOURCE: a hotspot
// that is a known reported fire's DIRECTIONAL downwind spread is demoted off urgent
// (RL ≤ 2) in fusion (connected-fire.ts), so a co-located fire + its in-cone hotspot no
// longer double-count and there is nothing to fold at render. (A crosswind/upwind hotspot
// near a known fire is a separate/potentially-new fire and CORRECTLY stays urgent — the
// old omnidirectional fold would have wrongly hidden it.)

/** Per-source counts of a queue set (the in-view side of the #146 subtraction). */
export type SourceCounts = Record<QueueSource, number>;
export function countBySource(items: QueueItem[]): SourceCounts {
  const c: SourceCounts = {
    hotspot: 0,
    cap: 0,
    smoke: 0,
    ice_storm: 0,
    flood: 0,
    media_signal: 0,
    oc_fire: 0,
  };
  for (const it of items) c[it.source] += 1;
  return c;
}

/**
 * In-view per-source counts for the "▲ N outside view" REDS subtraction (#146,
 * #383 part-1, #376). Identical to countBySource EXCEPT the two SCORED red sources —
 * hotspot (#383 part-1) and CAP (#376) — are gated to the urgent band (response_level
 * ≥ URGENT_MIN_RESPONSE_LEVEL), because:
 *   • the national side (`/api/anomaly-counts`) now counts only urgent reds (RL≥3) for
 *     BOTH, so the in-view side must too, or national − in-view is no longer reds−reds;
 *   • the queue LIST carries the #370-demoted L1/L2 candidates the urgent map never
 *     shows (and, for CAP, the RL2 squall/thunderstorm candidates), so counting them
 *     in-view here would over-subtract and make the chip UNDER-report (often hide) the
 *     reds that actually sit outside the frame — the opposite of the chip's job.
 * The other four sources already match their national predicate 1:1, so they pass
 * through countBySource unchanged. Ack state is irrelevant (the national count has
 * no ack filter) — gate on response level alone.
 */
export function countUrgentBySource(items: QueueItem[]): SourceCounts {
  const c = countBySource(items);
  c.hotspot = items.filter(
    (it) => it.source === 'hotspot' && (it.responseLevel ?? 0) >= URGENT_MIN_RESPONSE_LEVEL,
  ).length;
  c.cap = items.filter(
    (it) => it.source === 'cap' && (it.responseLevel ?? 0) >= URGENT_MIN_RESPONSE_LEVEL,
  ).length;
  // #444 — OC fires are SCORED reds: gate to the urgent band so national − in-view
  // stays reds−reds (the national /api/anomaly-counts oc_fire count gates RL≥3 too).
  c.oc_fire = items.filter(
    (it) => it.source === 'oc_fire' && (it.responseLevel ?? 0) >= URGENT_MIN_RESPONSE_LEVEL,
  ).length;
  return c;
}

/**
 * "N outside view" total (#146): national count − in-view count, per source,
 * clamped at zero, summed. The CAP source is excluded when EWW is muted so a
 * shelved class never resurfaces as an "outside view" red (it mirrors the queue's
 * own cap gating — the viewport fetch returns no CAP rows when !capVisible).
 */
export function outsideViewTotal(
  national: SourceCounts,
  inView: SourceCounts,
  capVisible: boolean,
): number {
  // oc_fire (#444) is independent of EWW visibility — an out-of-view urgent OC fire
  // must always count toward the chip, so it is in BOTH branches.
  const sources: QueueSource[] = capVisible
    ? ['hotspot', 'cap', 'smoke', 'ice_storm', 'flood', 'media_signal', 'oc_fire']
    : ['hotspot', 'smoke', 'ice_storm', 'flood', 'media_signal', 'oc_fire'];
  return sources.reduce((sum, s) => sum + Math.max(0, national[s] - inView[s]), 0);
}
